import { useCallback, useEffect, useRef, useState } from 'react'
import { NestAPI } from '@marketing-milk/frontend'
import {
  CreativeImportAutosaveRequest,
  CreativeImportValidationReport,
} from '@marketing-milk/interfaces'
import { RowUIState } from './state'

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/** The slice of page state the pre-confirm autosave persists. */
export interface AutosaveInput {
  creativeImportID: number | null
  confirmed: boolean
  report: CreativeImportValidationReport | null
  rows: Record<number, RowUIState>
  sprintID: number | null
  createPacket: boolean
}

/**
 * Shapes the full pre-confirm snapshot: EVERY row with an explicit `selected`
 * flag (so deselections persist) and the raw edited copy strings. Pure so it's
 * unit-testable. Falls back to the report seeds for any row missing UI state.
 */
export function buildAutosaveRequest(
  report: CreativeImportValidationReport,
  rows: Record<number, RowUIState>,
  sprintID: number | null,
  createPacket: boolean
): CreativeImportAutosaveRequest {
  return {
    sprintID,
    createPacket,
    rows: report.rows.map(row => {
      const ui = rows[row.rowNumber]
      return {
        rowNumber: row.rowNumber,
        selected: ui?.selected ?? false,
        name: ui?.editedName ?? row.effectiveName,
        primaryText: ui?.editedPrimaryText ?? '',
        headline: ui?.editedHeadline ?? '',
        description: ui?.editedDescription ?? '',
        callToAction: ui?.editedCallToAction ?? row.effectiveCallToAction,
        destinationUrl: ui?.editedDestinationURL ?? '',
        justification: ui?.editedJustification ?? '',
        // Format-specific, so absent (not empty) for rows that don't have them:
        // an empty slides array would read as an intentional clear on the server.
        slides: ui?.editedSlides,
        copyPools: ui?.editedCopyPools && { ...ui.editedCopyPools },
      }
    }),
  }
}

const AUTOSAVE_DELAY_MS = 800

/**
 * CR: debounced autosave of the pre-confirm Review edits. Saves whenever the
 * import is unconfirmed and has a persisted id (covers Review, Options, and the
 * pre-confirm Import step). Saves are serialized (each run persists the latest
 * snapshot, deduped by payload) so a burst of edits can't land out of order.
 * `flush()` forces an immediate save — call it before Confirm and on step
 * changes. The honest guarantee is "your last SAVED edit is preserved."
 */
export function useCreativeImportAutosave(
  businessID: number | string,
  input: AutosaveInput
): { status: AutosaveStatus; flush: () => Promise<void> } {
  const [status, setStatus] = useState<AutosaveStatus>('idle')

  const canSave = !input.confirmed && input.creativeImportID !== null && input.report !== null
  const payload =
    canSave && input.report
      ? buildAutosaveRequest(input.report, input.rows, input.sprintID, input.createPacket)
      : null

  // Latest snapshot + id, read at save time so a serialized run always PATCHes
  // the newest state rather than whatever was current when it was scheduled.
  const latestRef = useRef<{ id: number; payload: CreativeImportAutosaveRequest } | null>(null)
  latestRef.current =
    payload && input.creativeImportID !== null ? { id: input.creativeImportID, payload } : null

  const lastSavedRef = useRef<string>('')
  const chainRef = useRef<Promise<void>>(Promise.resolve())
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const cancelTimer = () => {
    if (timerRef.current !== undefined) clearTimeout(timerRef.current)
  }

  const saveNow = useCallback(async (): Promise<void> => {
    const current = latestRef.current
    if (!current) return
    const serialized = JSON.stringify({ businessID, ...current })
    if (serialized === lastSavedRef.current) return
    setStatus('saving')
    try {
      await NestAPI.patch(`businesses/${businessID}/creative-import/${current.id}`, current.payload)
      lastSavedRef.current = serialized
      setStatus('saved')
    } catch {
      // Leave lastSaved untouched so the next attempt retries this payload.
      setStatus('error')
    }
  }, [businessID])

  // Serialize saves onto one chain; each link persists the latest snapshot.
  const enqueueSave = useCallback((): Promise<void> => {
    chainRef.current = chainRef.current.catch(() => undefined).then(() => saveNow())
    return chainRef.current
  }, [saveNow])

  const serialized =
    payload && input.creativeImportID !== null
      ? JSON.stringify({ businessID, id: input.creativeImportID, payload })
      : null
  useEffect(() => {
    if (serialized === null || serialized === lastSavedRef.current) return
    timerRef.current = setTimeout(() => void enqueueSave(), AUTOSAVE_DELAY_MS)
    return cancelTimer
  }, [serialized, enqueueSave])

  const flush = useCallback(async (): Promise<void> => {
    cancelTimer()
    await enqueueSave()
  }, [enqueueSave])

  return { status, flush }
}
